import MedicationAnswer from "./MedicationAnswer"
import MedicationStringCreator, {
    resolveString
} from "./MedicationStringCreator"
import MedicationQuestionnaireInput from "./MedicationQuestionnaireInput"
import expect from "../../util/expect"
import notNull from "../../util/notNull"
import unique from "../../util/unique"

type AutomaticAnswerHandler = (
    props: MedicationQuestionnaireInput,
    problematicATCs?: string[]
) => MedicationAnswer

class MedicationQuestion {
    private info: MedicationQuestionnaireInput
    private content: string
    private answers: MedicationAnswer[]
    private problematicATCs: string[]
    private next: MedicationQuestion | null
    private handler: AutomaticAnswerHandler | null

    private manualAnswerCondition: (input: MedicationQuestionnaireInput) => boolean

    constructor(
        content: MedicationStringCreator,
        info: MedicationQuestionnaireInput
    ) {
        this.problematicATCs = []
        this.info = info
        this.content = resolveString(content, info)
        this.answers = []
        this.handler = null
        this.next = null
        this.manualAnswerCondition = () => false
    }

    addProblematicACTs(act: string | string[]) {
        if (Array.isArray(act)) this.problematicATCs.push(...act)
        else this.problematicATCs.push(act)
        this.problematicATCs = unique(this.problematicATCs)
        return this.pushACTsToChildren()
    }

    pushACTsToChildren() {
        if (this.next) this.next.addProblematicACTs(this.problematicATCs)
        for (const answer of this.answers)
            answer.addProblematicACTs(this.problematicATCs)
        return this
    }

    getProblematicACTs() {
        return this.problematicATCs
    }

    attachAnswer(answer: MedicationAnswer): this {
        this.answers.push(answer.inject(this.info))
        this.pushACTsToChildren()
        return this
    }

    answerAutomatically(handler: AutomaticAnswerHandler): this {
        this.handler = handler
        return this
    }

    dontAnswerAutomaticallyIf(condition: (input: MedicationQuestionnaireInput) => boolean): this {
        this.manualAnswerCondition = condition
        return this
    }

    followedBy(question?: MedicationQuestion): this {
        if (question) this.next = question
        this.pushACTsToChildren()
        return this
    }

    hasFollowingQuestion(): boolean {
        return notNull(this.next)
    }

    expectFollowingQuestion(): MedicationQuestion {
        return expect(this.next)
    }

    isAnsweredAutomatically(props: MedicationQuestionnaireInput) {
        return notNull(this.handler) && !this.manualAnswerCondition(props)
    }

    getAutomatedAnswer(props: MedicationQuestionnaireInput): MedicationAnswer {
        return expect(this.handler)(props, this.problematicATCs)
            .inject(props)
            .addProblematicACTs(this.getProblematicACTs())
    }

    get(): string {
        return this.content
    }

    getPossibleAnswers(): string[] {
        return this.answers.map((a) => a.get()).sort()
    }

    getAnswerByName(name: string): MedicationAnswer {
        const answer = this.answers.find((a) => a.get() === name)
        return expect(answer)
    }
}

export default MedicationQuestion
